import { FC } from "react";

import { type CourseAssessmentSummaryData } from "api/api-data-types";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { Link } from "utils/routing";
import { courseHomeUrl } from "utils/urls";

type AssessmentSummaryTableProps = {
  data: CourseAssessmentSummaryData[];
};

export const AssessmentSummaryTable: FC<AssessmentSummaryTableProps> = ({ data }) => {
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  const formatPercentage = (value: number) => {
    return `${value}%`;
  };

  return (
    <div className="overflow-x-auto">
      <div className="max-h-[400px] overflow-y-auto">
        <table className="mds-table w-full">
          <thead className="sticky top-0 z-10 bg-white shadow-sm">
            <tr>
              <th className="w-[72px]">
                <Tooltip place="top" text={t("assessment_summary_report.columns.code_tooltip")}>
                  {t("assessment_summary_report.columns.code")}
                </Tooltip>
              </th>

              <th className="w-[160px]">
                <Tooltip
                  place="top"
                  text={t("assessment_summary_report.columns.course_title_tooltip")}
                >
                  {t("assessment_summary_report.columns.course_title")}
                </Tooltip>
              </th>

              <th className="w-[72px]">
                <Tooltip
                  place="top"
                  text={t("assessment_summary_report.columns.instructors_tooltip")}
                >
                  {t("assessment_summary_report.columns.instructors")}
                </Tooltip>
              </th>

              <th className="w-[48px]">
                <Tooltip
                  place="top"
                  text={t("assessment_summary_report.columns.last_assessed_tooltip")}
                >
                  {t("assessment_summary_report.columns.last_assessed")}
                </Tooltip>
              </th>

              <th className="w-[40px]">
                <Tooltip
                  place="top"
                  text={t("assessment_summary_report.columns.percent_assessed_tooltip")}
                >
                  {t("assessment_summary_report.columns.percent_assessed")}
                </Tooltip>
              </th>

              <th className="w-[48px]">
                <Tooltip
                  place="top"
                  text={t("assessment_summary_report.columns.percent_with_comments_tooltip")}
                >
                  {t("assessment_summary_report.columns.percent_with_comments")}
                </Tooltip>
              </th>

              <th className="w-[40px]">
                <Tooltip
                  place="top"
                  text={t("assessment_summary_report.columns.comment_length_tooltip")}
                >
                  {t("assessment_summary_report.columns.comment_length")}
                </Tooltip>
              </th>

              <th className="w-[32px]">
                <Tooltip
                  place="top"
                  text={t("assessment_summary_report.columns.wait_time_tooltip")}
                >
                  {t("assessment_summary_report.columns.wait_time")}
                </Tooltip>
              </th>

              <th className="w-[56px]">
                <Tooltip
                  place="top"
                  text={t("assessment_summary_report.columns.assessments_per_student_tooltip")}
                >
                  {t("assessment_summary_report.columns.assessments_per_student")}
                </Tooltip>
              </th>
            </tr>
          </thead>

          <tbody>
            {data.map((row) => (
              <tr key={row.id}>
                <td className="w-[72px] truncate pr-[1px]">
                  <Tooltip
                    place="left-start"
                    text={row.course_code_title}
                    wrapperClassName="truncate"
                  >
                    {row.course_code_title}
                  </Tooltip>
                </td>

                <td className="w-[160px] pr-[1px]">
                  <Tooltip place="left-start" text={row.title}>
                    <Link
                      className="block text-blue hover:text-blue-shade-40"
                      title={row.title}
                      to={`${courseHomeUrl(row.id)}?tab=assessments`}
                    >
                      {row.title}
                    </Link>
                  </Tooltip>
                </td>

                <td className="w-[72px] truncate pr-[1px]">
                  <Tooltip
                    place="left-start"
                    text={row.instructor_names.join(", ")}
                    wrapperClassName="truncate"
                  >
                    {row.instructor_names.join(", ")}
                  </Tooltip>
                </td>

                <td className="w-[48px] pr-[1px] text-center">
                  {formatDate(row.date_last_assessed)}
                </td>
                <td className="w-[40px] pr-[1px] text-right">
                  {formatPercentage(row.assessment_percentage)}
                </td>
                <td className="w-[40px] pr-[1px] text-right">
                  {row.assessment_percentage > 0 ? formatPercentage(row.comment_percentage) : "-"}
                </td>
                <td className="w-[40px] pr-[1px] text-right">
                  {row.assessment_percentage > 0 ? row.avg_comment_length : "-"}
                </td>
                <td className="w-[32px] pr-[1px] text-right">
                  {row.assessment_percentage > 0 ? row.avg_assessment_delay_days : "-"}
                </td>
                <td className="w-[56px] text-right">
                  {row.assessment_percentage > 0 ? row.avg_student_assessments : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
